import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit2, Pause, Play, Plus, TrendingUp, Users, XCircle } from 'react-feather';
import { twMerge } from 'tailwind-merge';
import { BarChart, BarChartProps } from '@mui/x-charts/BarChart';
import moment from 'moment';
import {
  CollectionSequenceBuyerProgress,
  CollectionSequenceBuyerProgressStatus,
  CollectionSequenceCreate,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCollectionSequences,
  updateCollectionSequence,
  enrollCollectionSequenceEnrollment,
  pauseCollectionSequenceEnrollment,
  resumeCollectionSequenceEnrollment,
  unenrollCollectionSequenceEnrollment,
} from '../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../redux/services/collectionTriggerService';
import { getSupplierSummary } from '../../../../redux/services/suppliersService';
import { getSupplierBuyers } from '../../../../redux/services/supplierBuyersService';
import { buildSupplierPath } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/colors';
import { DillButton, DillTable, DillDropDownFilter, DillCheckbox } from '../../../general/Dill';
import type { DillColumn } from '../../../general/Dill';
import { useCollectionSequenceBuyerProgressForSequence } from './useCollectionSequenceBuyerProgressForSequence';
import { useCollectionBuyerEngagements } from './useCollectionBuyerEngagements';
import { EnrollCustomersModal } from './EnrollCustomersModal';
import { CollectionSequenceFormModal } from './CollectionSequenceFormModal';

const STATUS_LABELS: Record<CollectionSequenceBuyerProgressStatus, string> = {
  ACTIVE: 'In Progress',
  PAUSED: 'Paused',
  COMPLETED: 'Finished',
  UNENROLLED: 'Unenrolled',
};

const STATUS_COLORS: Record<CollectionSequenceBuyerProgressStatus, string> = {
  ACTIVE: 'bg-blue-100 text-blue-800',
  PAUSED: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  UNENROLLED: 'bg-gray-100 text-gray-600',
};

const getResponsiveConfig = (): Partial<BarChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  return {
    height: isMobile ? 280 : 320,
    margin: {
      left: isMobile ? 40 : 50,
      right: isMobile ? 15 : 20,
      top: 20,
      bottom: isMobile ? 40 : 40,
    },
    hideLegend: false,
  };
};

// Mirrors resolveTemplate in collectionSequenceService.ts, so a step's title/description shows
// the same way here as it does on the task it actually creates, instead of the raw
// {{buyerName}}/{{amount}} tokens.
const resolveTemplate = (
  template: string | null | undefined,
  tokens: { buyerName: string; amount: number }
): string =>
  (template || '')
    .replace(/{{\s*buyerName\s*}}/g, tokens.buyerName)
    .replace(/{{\s*amount\s*}}/g, `$${tokens.amount.toFixed(2)}`);

const createCheckboxItem = (label: string, value: string, isChecked: boolean) => ({
  label,
  value,
  labelInElement: (
    <div className="flex mx-1 my-1 items-center gap-2">
      <DillCheckbox isChecked={isChecked} />
      <div className="text-black text-xs">{label}</div>
    </div>
  ),
});

const StatTile = ({
  label,
  value,
  colorClass,
}: {
  label: string;
  value: number;
  colorClass: string;
}) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4">
    <p className="text-xs text-gray-400 m-0">{label}</p>
    <p className={`text-2xl font-semibold m-0 mt-1 ${colorClass}`}>{value}</p>
  </div>
);

type EnrollmentRow = CollectionSequenceBuyerProgress & { buyerName: string };

type DetailTab = 'PERFORMANCE' | 'ENROLLMENTS';

function CollectionSequenceDetails() {
  const { sequenceId } = useParams<{ sequenceId: string }>();
  const [activeTab, setActiveTab] = useState<DetailTab>('PERFORMANCE');
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollErrorMessage, setEnrollErrorMessage] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState<string | null>(null);
  const [updatingBuyerId, setUpdatingBuyerId] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id || '';
  const { sequences, loadingList } = useAppSelector((state) => state.collectionSequences);
  const { triggers } = useAppSelector((state) => state.collectionTriggers);
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const { supplierBuyers } = useAppSelector((state) => state.supplierBuyers);
  const { isBuyerAvailable, refresh: refreshEngagements } =
    useCollectionBuyerEngagements(supplierId);
  const {
    progressByBuyerId,
    isLoading,
    refresh: refreshProgress,
  } = useCollectionSequenceBuyerProgressForSequence(sequenceId || '', supplierId);

  useEffect(() => {
    if (supplierId && sequences.length === 0) {
      dispatch(getSupplierCollectionSequences({ supplierId }));
    }
  }, [supplierId, sequences.length, dispatch]);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierCollectionTriggers({ supplierId }));
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    if (supplierId && supplierUsers.length === 0) {
      dispatch(getSupplierSummary({ supplierId }));
    }
  }, [supplierId, supplierUsers.length, dispatch]);

  useEffect(() => {
    if (supplierId && supplierBuyers.length === 0) {
      dispatch(getSupplierBuyers({ supplierId }));
    }
  }, [supplierId, supplierBuyers.length, dispatch]);

  const sequence = useMemo(
    () => sequences.find((s) => s.id === sequenceId),
    [sequences, sequenceId]
  );
  const sortedSteps = useMemo(
    () => (sequence ? [...sequence.steps].sort((a, b) => a.order - b.order) : []),
    [sequence]
  );

  const buyerNameById = useMemo(() => {
    const map = new Map<string, string>();
    supplierBuyers.forEach((b) => map.set(b.id, b.name || 'Unnamed customer'));
    return map;
  }, [supplierBuyers]);

  const enrollments = useMemo<EnrollmentRow[]>(
    () =>
      Object.values(progressByBuyerId).map((p) => ({
        ...p,
        buyerName: buyerNameById.get(p.buyerId) || 'Unnamed customer',
      })),
    [progressByBuyerId, buyerNameById]
  );

  const filteredEnrollments = useMemo(() => {
    if (!selectedFilters?.status?.length) return enrollments;
    return enrollments.filter((row) => selectedFilters.status.includes(row.status));
  }, [enrollments, selectedFilters]);

  const enrollmentStatusOptions = useMemo(
    () => Array.from(new Set(enrollments.map((row) => row.status))).sort(),
    [enrollments]
  );

  const filterItems = useMemo(
    () => [
      {
        id: 'status',
        label: 'Filter by Status',
        value: enrollmentStatusOptions.map((status) =>
          createCheckboxItem(
            STATUS_LABELS[status],
            status,
            selectedFilters?.status?.includes(status) ?? false
          )
        ),
      },
    ],
    [enrollmentStatusOptions, selectedFilters]
  );

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<CollectionSequenceBuyerProgressStatus, number> = {
      ACTIVE: 0,
      PAUSED: 0,
      COMPLETED: 0,
      UNENROLLED: 0,
    };
    enrollments.forEach((e) => {
      counts[e.status] = (counts[e.status] ?? 0) + 1;
    });
    return counts;
  }, [enrollments]);

  // Only ACTIVE/PAUSED enrollments have a meaningful "current step" - once an enrollment
  // finishes or is unenrolled, currentStepIndex advances past the last step (see
  // advanceBuyerSequenceProgress in collectionSequenceService.ts), so those are represented in
  // the stat tiles above rather than a step bucket here.
  const stepChartData = useMemo(
    () =>
      sortedSteps.map((step, index) => ({
        stepLabel: `Step ${index + 1}`,
        inProgress: enrollments.filter(
          (e) => e.status === 'ACTIVE' && e.currentStepIndex === step.order
        ).length,
        paused: enrollments.filter(
          (e) => e.status === 'PAUSED' && e.currentStepIndex === step.order
        ).length,
      })),
    [sortedSteps, enrollments]
  );

  const buyerOptions = useMemo(
    () =>
      supplierBuyers
        .filter((b) => isBuyerAvailable(b.id))
        .map((b) => ({ label: b.name || 'Unnamed customer', value: b.id })),
    [supplierBuyers, isBuyerAvailable]
  );
  const unavailableCount = supplierBuyers.length - buyerOptions.length;

  const handleOpenEnrollModal = () => {
    setEnrollErrorMessage(null);
    setIsEnrollModalOpen(true);
    refreshEngagements();
  };

  const handleCloseEnrollModal = () => {
    setIsEnrollModalOpen(false);
    setEnrollErrorMessage(null);
  };

  const handleEnrollSubmit = async (buyerIds: string[]) => {
    if (!sequence || buyerIds.length === 0) return;
    setEnrollErrorMessage(null);
    setIsEnrolling(true);
    const results = await Promise.all(
      buyerIds.map((buyerId) =>
        dispatch(
          enrollCollectionSequenceEnrollment({ supplierId, sequenceId: sequence.id, buyerId })
        )
      )
    );
    setIsEnrolling(false);

    const failedCount = results.filter((result) => {
      const payload = result.payload as { success?: boolean } | null;
      return !payload || payload.success === false;
    }).length;

    if (failedCount > 0) {
      setEnrollErrorMessage(
        failedCount === buyerIds.length
          ? 'Failed to enroll. Please try again.'
          : `Enrolled ${buyerIds.length - failedCount} of ${
              buyerIds.length
            } - some customers could not be enrolled.`
      );
    }
    if (failedCount < buyerIds.length) {
      refreshEngagements();
      refreshProgress();
    }
    if (failedCount === 0) {
      handleCloseEnrollModal();
    }
  };

  const handleOpenEditModal = () => {
    setEditErrorMessage(null);
    setIsEditModalOpen(true);
    refreshEngagements();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditErrorMessage(null);
  };

  const handleEditSubmit = async (data: CollectionSequenceCreate) => {
    if (!sequence) return;
    setEditErrorMessage(null);
    const result = await dispatch(
      updateCollectionSequence({ supplierId, sequenceId: sequence.id, data })
    );
    const payload = result.payload as { success?: boolean } | string | null;
    const failed = !payload || (typeof payload === 'object' && payload.success === false);
    if (failed) {
      setEditErrorMessage('Failed to save. Please try again.');
      return;
    }
    handleCloseEditModal();
    dispatch(getSupplierCollectionSequences({ supplierId }));
    refreshEngagements();
    refreshProgress();
  };

  const handlePauseEnrollment = async (buyerId: string) => {
    if (!sequence) return;
    setUpdatingBuyerId(buyerId);
    await dispatch(
      pauseCollectionSequenceEnrollment({ supplierId, sequenceId: sequence.id, buyerId })
    );
    setUpdatingBuyerId(null);
    refreshProgress();
  };

  const handleResumeEnrollment = async (buyerId: string) => {
    if (!sequence) return;
    setUpdatingBuyerId(buyerId);
    await dispatch(
      resumeCollectionSequenceEnrollment({ supplierId, sequenceId: sequence.id, buyerId })
    );
    setUpdatingBuyerId(null);
    refreshProgress();
  };

  const handleUnenrollEnrollment = async (buyerId: string) => {
    if (!sequence) return;
    setUpdatingBuyerId(buyerId);
    await dispatch(
      unenrollCollectionSequenceEnrollment({ supplierId, sequenceId: sequence.id, buyerId })
    );
    setUpdatingBuyerId(null);
    refreshProgress();
    refreshEngagements();
  };

  const columns: DillColumn<EnrollmentRow>[] = useMemo(
    () => [
      {
        key: 'buyerName',
        label: 'Customer',
        width: 200,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.buyerName,
        render: (_: any, row: EnrollmentRow) => (
          <p className="text-xs text-gray-900">{row.buyerName}</p>
        ),
      },
      {
        key: 'currentStep',
        label: 'Current Step',
        width: 220,
        sortable: true,
        getSortValue: (row) => row.currentStepIndex,
        render: (_: any, row: EnrollmentRow) => {
          const step = sortedSteps[row.currentStepIndex];
          const amount =
            typeof row.triggerContext?.amount === 'number' ? row.triggerContext.amount : 0;
          const title = step
            ? resolveTemplate(step.title, { buyerName: row.buyerName, amount })
            : null;
          return (
            <p className="text-xs text-gray-700">
              {title
                ? `Step ${row.currentStepIndex + 1} — ${title}`
                : row.status === 'COMPLETED'
                ? 'Finished'
                : '—'}
            </p>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.status,
        render: (_: any, row: EnrollmentRow) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[row.status]}`}>
            {STATUS_LABELS[row.status]}
          </span>
        ),
      },
      {
        key: 'startedAt',
        label: 'Enrolled',
        width: 120,
        sortable: true,
        getSortValue: (row) => (row.startedAt ? new Date(row.startedAt).getTime() : 0),
        render: (_: any, row: EnrollmentRow) => (
          <p className="text-xs text-gray-500">
            {row.startedAt ? moment(row.startedAt).format('MM/DD/YYYY') : '-'}
          </p>
        ),
      },
      {
        key: 'nextStepDueAt',
        label: 'Next Step Due',
        width: 130,
        sortable: true,
        getSortValue: (row) => (row.nextStepDueAt ? new Date(row.nextStepDueAt).getTime() : 0),
        render: (_: any, row: EnrollmentRow) =>
          row.status === 'ACTIVE' && row.nextStepDueAt ? (
            <p className="text-xs text-gray-500">
              {moment(row.nextStepDueAt).format('MM/DD/YYYY')}
            </p>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          ),
      },
      {
        key: 'actions',
        label: '',
        width: 190,
        sortable: false,
        render: (_: any, row: EnrollmentRow) => {
          if (row.status !== 'ACTIVE' && row.status !== 'PAUSED') {
            return <span className="text-xs text-gray-300">—</span>;
          }
          const isUpdating = updatingBuyerId === row.buyerId;
          return (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {row.status === 'ACTIVE' ? (
                <DillButton
                  variant="secondary"
                  label="Pause"
                  icon={<Pause size={12} />}
                  height={28}
                  width={88}
                  isDisabled={isUpdating}
                  onClick={() => handlePauseEnrollment(row.buyerId)}
                />
              ) : (
                <DillButton
                  variant="secondary"
                  label="Resume"
                  icon={<Play size={12} />}
                  height={28}
                  width={88}
                  isDisabled={isUpdating}
                  onClick={() => handleResumeEnrollment(row.buyerId)}
                />
              )}
              <DillButton
                variant="danger"
                label="Unenroll"
                icon={<XCircle size={12} />}
                height={28}
                width={88}
                isDisabled={isUpdating}
                onClick={() => handleUnenrollEnrollment(row.buyerId)}
              />
            </div>
          );
        },
      },
    ],
    [sortedSteps, updatingBuyerId]
  );

  if (!sequence) {
    return (
      <div className="w-full h-full pt-4 px-4">
        <p className="text-xs text-gray-400">{isLoading ? 'Loading…' : 'Sequence not found.'}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full pt-4 px-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 rounded-xl bg-gray-200 w-fit">
          {(
            [
              { id: 'PERFORMANCE', label: 'Performance', icon: <TrendingUp size={14} /> },
              {
                id: 'ENROLLMENTS',
                label: `Enrollments (${enrollments.length})`,
                icon: <Users size={14} />,
              },
            ] as { id: DetailTab; label: string; icon: React.ReactNode }[]
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={twMerge(
                'flex items-center justify-center gap-1.5 h-[34px] text-xs font-medium rounded-lg border-none cursor-pointer transition-all px-4',
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'bg-transparent text-gray-500 hover:text-gray-800'
              )}>
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DillButton
            variant="secondary"
            label="Edit"
            icon={<Edit2 size={14} />}
            onClick={handleOpenEditModal}
            height={34}
          />
          <DillButton
            label="Enroll customers"
            icon={<Plus size={14} />}
            onClick={handleOpenEnrollModal}
            height={34}
          />
        </div>
      </div>

      {activeTab === 'PERFORMANCE' && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatTile
              label="Total Enrolled"
              value={enrollments.length}
              colorClass="text-gray-900"
            />
            <StatTile label="In Progress" value={statusCounts.ACTIVE} colorClass="text-blue-600" />
            <StatTile label="Paused" value={statusCounts.PAUSED} colorClass="text-amber-600" />
            <StatTile label="Finished" value={statusCounts.COMPLETED} colorClass="text-green-600" />
            <StatTile
              label="Unenrolled"
              value={statusCounts.UNENROLLED}
              colorClass="text-gray-500"
            />
          </div>

          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-900 m-0 mb-1">
              Enrollment status by step
            </p>
            <p className="text-xs text-gray-400 m-0 mb-2">
              How many customers are currently at each step.
            </p>
            {stepChartData.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8">This sequence has no steps.</p>
            ) : (
              <BarChart
                borderRadius={8}
                dataset={stepChartData}
                xAxis={[
                  {
                    dataKey: 'stepLabel',
                    tickLabelStyle: {
                      fontSize: 12,
                      fill: COLORS.GREY_500,
                      fontFamily: 'Inter',
                      fontWeight: 'bold',
                    },
                  },
                ]}
                series={[
                  { dataKey: 'inProgress', label: 'In Progress', color: COLORS.INFO_500 },
                  { dataKey: 'paused', label: 'Paused', color: COLORS.WARNING_500 },
                ]}
                yAxis={[
                  {
                    tickLabelStyle: { fontSize: 11, fill: COLORS.GREY_500, fontFamily: 'Inter' },
                  },
                ]}
                grid={{ horizontal: true }}
                {...getResponsiveConfig()}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'ENROLLMENTS' && (
        <div className="h-[calc(100vh-150px)]">
          <DillTable<EnrollmentRow>
            columns={columns}
            data={filteredEnrollments}
            rowKey={(row) => row.buyerId}
            sortable
            resizable
            search
            searchPlaceholder="Search customers…"
            showActiveFilters
            toolbarLeft={
              <div className="w-[220px]">
                <DillDropDownFilter
                  placeholder="Filters"
                  closeMenu={false}
                  isMulti
                  showSelectedItems
                  multiValue={selectedFilters}
                  items={filterItems}
                  showMinimalDesign
                  onMultiSelectChange={handleFilterChange}
                  inputHeight={32}
                />
              </div>
            }
            onRowClick={(row) =>
              navigate(buildSupplierPath(supplierId, `/customers/${row.buyerId}`))
            }
            emptyMessage="No customers enrolled yet."
            showCount={false}
            striped={false}
          />
        </div>
      )}

      <EnrollCustomersModal
        open={isEnrollModalOpen}
        onClose={handleCloseEnrollModal}
        onSubmit={handleEnrollSubmit}
        buyerOptions={buyerOptions}
        unavailableCount={unavailableCount}
        isSubmitting={isEnrolling}
        errorMessage={enrollErrorMessage}
      />

      <CollectionSequenceFormModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleEditSubmit}
        initialData={sequence}
        isLoading={loadingList.includes('updateCollectionSequence')}
        errorMessage={editErrorMessage}
        supplierUsers={supplierUsers}
        supplierBuyers={supplierBuyers}
        triggers={triggers}
        isBuyerAvailable={isBuyerAvailable}
      />
    </div>
  );
}

export default CollectionSequenceDetails;
